import api from '../Api';
import { notifyBase } from '../config';

/**
 * Exchange the ERP token for a Notify session and read what that user is allowed
 * to see, returned as `notify:*` pseudo-permissions the sidebar gates on.
 * Extensions/Approvals come from Notify's `sections`; Performance mirrors the
 * separate `perf_enabled` flag (every user sees at least their own perf when
 * it's on). Fails soft (returns []): if Notify is unreachable the ERP simply
 * shows no Notify menu.
 */
async function resolveNotifyAccess(erpToken) {
  try {
    const ex = await fetch(`${notifyBase}/api/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ erp_token: erpToken }),
    });
    if (!ex.ok) return { status: 'down', perms: [] };
    const { access_token: notifyToken } = await ex.json();

    const me = await fetch(`${notifyBase}/api/access/me`, {
      headers: { Authorization: `Bearer ${notifyToken}` },
    });
    if (!me.ok) return { status: 'down', perms: [] };
    const access = await me.json();

    const perms = (access.sections || []).map(s => `notify:${s}`);
    if (access.perf_enabled && !perms.includes('notify:perf')) {
      perms.push('notify:perf');
    }
    return { status: 'ready', perms };
  } catch (e) {
    return { status: 'down', perms: [] };
  }
}

/**
 * Notify's RBAC drives the ERP's Notify menu — single source of truth, no
 * duplicated ERP permissions. Never blocks ERP app load on Notify: the whole
 * handshake is capped at 5s (after which it's treated as 'down').
 */
async function fetchNotifyAccess(erpToken) {
  const timeout = new Promise(resolve =>
    setTimeout(() => resolve({ status: 'down', perms: [] }), 5000)
  );
  return Promise.race([resolveNotifyAccess(erpToken), timeout]);
}

export const login =
  (body = {}) =>
  async dispatch => {
    const { data } = await api.post('/auth/login', body);

    localStorage.setItem('token', data.token);

    const basePermissions = data.permissions || [];

    // Log the user in IMMEDIATELY — ERP login is never blocked by Notify.
    dispatch({
      type: 'FETCH_TOKEN',
      payload: { type: true, user: data.user, permissions: basePermissions },
    });
    // Show the Notify menu group in a 'loading' state right away so it never
    // looks missing during the (seconds-long in prod) background fetch.
    dispatch({ type: 'SET_NOTIFY_STATUS', payload: 'loading' });

    // Then, in the background, ask Notify what this user may see and merge it
    // into the menu when it arrives. Fully fail-soft: on any error/slowness the
    // menu shows an 'unavailable' state instead of vanishing. Never awaited, so
    // it cannot affect ERP login latency or success even if Notify is down.
    fetchNotifyAccess(data.token)
      .then(({ status, perms }) => {
        if (status === 'ready' && perms.length) {
          dispatch({
            type: 'FETCH_TOKEN',
            payload: {
              type: true,
              user: data.user,
              permissions: [...basePermissions, ...perms],
            },
          });
        }
        // FETCH_TOKEN replaces auth state, so set the status AFTER it.
        dispatch({ type: 'SET_NOTIFY_STATUS', payload: status });
      })
      .catch(() => dispatch({ type: 'SET_NOTIFY_STATUS', payload: 'down' }));
  };

export const logout = () => {
  localStorage.clear('token');
  return {
    type: 'REMOVE_TOKEN',
  };
};
